import { getEnvironmentActivitiesRequest } from "Api/activity/getEnvironmentActivities.request";
import { ACTIVITY_TYPES } from "Constants/constants";
import { createAppAsyncThunk } from "Store/createAppAsyncThunk";

import type { Activity } from "Api/activity/types";

export interface LoadTaskActivitiesArgs {
  projectId: string;
  environmentId: string;
  count?: number;
}

/**
 * Loads every `environment.task` activity for an environment in ONE request.
 *
 * The activities endpoint has no per-task filter — it rejects an unknown `task`
 * query key with a 400 (verified live; the `cron` key is whitelisted, `task` is
 * not). So we fetch the whole task-typed stream server-side and split it per
 * task in `selectTaskActivities`. This is a stopgap: a backend `task` filter
 * (mirroring `cron=<name>`) is requested — it won't scale to environments with
 * many tasks. Until then the type filter keeps the payload to task activities
 * only, so it stays one narrow request regardless of how many tasks exist.
 */
export const loadTaskActivities = createAppAsyncThunk<
  {
    projectId: string;
    environmentId: string;
    activities: Activity[];
  },
  LoadTaskActivitiesArgs
>("activity/task/load", async ({ projectId, environmentId, count = 25 }) => {
  const activities = await getEnvironmentActivitiesRequest({
    projectId,
    encodedEnvId: encodeURIComponent(environmentId),
    filters: ACTIVITY_TYPES.ENVIRONMENT.TASK,
    count
  });

  return { projectId, environmentId, activities };
});
